import {RefObject, useEffect, useRef, useState} from 'react';
import {useRouter} from 'next/router';
import {Box, Container, Image, Text, IconButton, IconProps, Button, SpaceProps} from '@chakra-ui/react';
import {ChevronLeftIcon, ChevronRightIcon} from '@chakra-ui/icons';
import {getLanguage} from '@eksab/util/helpers';

interface ScrollableFiltersProps {
  items: {
    id: number;
    name: string;
    icon?: string;
  }[];
  onItemClick?: (item: ScrollableFiltersProps['items'][number]) => void;
  activeItem?: string | null;
}

const GUTTER: SpaceProps['m'] = 1.5;

export const ScrollableFilters = ({items, onItemClick, activeItem = null}: ScrollableFiltersProps) => {
  const scrollableAreaRef = useRef<HTMLDivElement>(null);
  const [showArrows, setShowArrows] = useState(true);

  useEffect(() => {
    const containerWidth = scrollableAreaRef.current?.parentElement?.getBoundingClientRect().width ?? 0;
    const filtersWidth = [...(scrollableAreaRef.current?.children ?? [])].reduce(
      (acc, child) => acc + child.getBoundingClientRect().width,
      0,
    );
    const totalGutter = (scrollableAreaRef.current?.childElementCount ?? 0) * (GUTTER * 4);

    setShowArrows(filtersWidth + totalGutter > containerWidth);
  }, [items]);

  return (
    <Box bg="gray.600" maxW="unset" opacity={activeItem ? 1 : 0.6} _hover={{opacity: 1}} transition="opacity 0.3s">
      <Container alignItems="center" gap="2" px={[3.5, null, null, '3.75rem']}>
        <ScrollArrow pos="start" visible={showArrows} scrollableAreaRef={scrollableAreaRef} />

        <Container overflow="clip" px="2" w="85%" flex={1}>
          <Container variant="scroll" gap={GUTTER} py="4" alignItems="center" ref={scrollableAreaRef}>
            {items.map((item) => (
              <Button
                key={item.id}
                variant="black"
                alignItems="center"
                gap="1.5"
                rounded="md"
                shadow="xs"
                isActive={item.name === activeItem}
                _hover={{transform: 'scale(1.05)'}}
                onClick={() => onItemClick?.(item)}
              >
                {item.icon && <Image maxW="9" h="auto" src={item.icon} alt={item.name} />}

                <Text color="inherit" noOfLines={1}>
                  {item.name}
                </Text>
              </Button>
            ))}
          </Container>
        </Container>

        <ScrollArrow pos="end" visible={showArrows} scrollableAreaRef={scrollableAreaRef} />
      </Container>
    </Box>
  );
};

interface ScrollArrowProps {
  pos: 'start' | 'end';
  visible: boolean;
  scrollableAreaRef: RefObject<HTMLDivElement>;
}

const SCROLL_DISTANCE = 150;

const ScrollArrow = ({pos, visible, scrollableAreaRef}: ScrollArrowProps) => {
  const {locale} = useRouter();

  const dir = getLanguage(locale) === 'en' ? pos : pos === 'start' ? 'end' : 'start';

  const scroll = () =>
    scrollableAreaRef?.current?.scrollBy({
      left: SCROLL_DISTANCE * (dir === 'end' ? 1 : -1),
      behavior: 'smooth',
    });

  const ChevronIcon = (props: IconProps) =>
    dir === 'start' ? <ChevronLeftIcon {...props} /> : <ChevronRightIcon {...props} />;

  return (
    <IconButton
      display={['none', null, 'inline-flex']}
      visibility={{md: visible ? 'visible' : 'hidden'}}
      opacity={[0, null, visible ? 1 : 0]}
      transition="opacity 0.5s, visibility 0.5s"
      boxSize="10"
      shadow="md"
      bgColor="white"
      colorScheme="gray"
      aria-label="Scroll"
      icon={<ChevronIcon boxSize="7" color="black" />}
      onClick={scroll}
    />
  );
};
